import {
	addDoc,
	collection,
	deleteDoc,
	doc,
	getDoc,
	getDocs,
	limit,
	orderBy,
	query,
	updateDoc,
} from "firebase/firestore";
import { firestore } from "../App";

export async function getDocument(collectionName, docId) {
	const docRef = doc(firestore, collectionName, docId);
	const docSnap = await getDoc(docRef);
	if (docSnap.exists()) {
		console.log("Document data:", docSnap.data());
		return docSnap.data();
	}
	console.log("No such document!");
	return null;
}

/**
 * Adds a new document to the specified collection.
 * @param {string} collectionName - The name of the collection.
 * @param {Object} data - The data to add to the document.
 * @returns {Promise<DocumentReference>} The reference to the added document.
 */
export async function pushDocument(collectionName, data) {
    const docRef = await addDoc(collection(firestore, collectionName), data);
    console.log("Document added with ID:", docRef.id);
    return docRef;
}

/**
 * Updates an existing document in the specified collection.
 * @param {string} collectionName - The name of the collection.
 * @param {string} docId - The ID of the document to update.
 * @param {Object} data - The new data for the document.
 * @returns {Promise<void>} A promise that resolves when the document is updated.
 */
export async function updateDocument(collectionName, docId, data) {
    const docRef = doc(firestore, collectionName, docId);
    await updateDoc(docRef, data);
    console.log("Document updated with ID:", docId);
}

/**
 * Deletes a document from the specified collection.
 * @param {string} collectionName - The name of the collection.
 * @param {string} docId - The ID of the document to delete.
 * @returns {Promise<void>} A promise that resolves when the document is deleted.
 */
export async function deleteDocument(collectionName, docId) {
    const docRef = doc(firestore, collectionName, docId);
    await deleteDoc(docRef);
    console.log("Document deleted with ID:", docId);
}

/* Events functions [START] */

export async function getEvent(docId) {
	return await getDocument("events", docId);
}

/**
 * Adds a new event document.
 * @param {Object} data - The data for the event.
 * @returns {Promise<Object|null>} The added event document data if successful, otherwise null.
 */
export async function pushEvent(data) {
	return await pushDocument("events", data);
}

/**
 * Updates an existing event document.
 * @param {string} docId - The ID of the event document to update.
 * @param {Object} data - The new data for the event.
 * @returns {Promise<Object|null>} The updated event document data if successful, otherwise null.
 */
export async function updateEvent(docId, data) {
	return await updateDocument("events", docId, data);
}

/**
 * Deletes an event document.
 * @param {string} docId - The ID of the event document to delete.
 * @returns {Promise<Object|null>} The deleted event document data if successful, otherwise null.
 */
export async function deleteEvent(docId) {
	return await deleteDocument("events", docId);
}
/* Events functions [END] */

/* Notes functions [START] */

/**
 * Adds a new note document.
 * @param {Object} data - The data for the note.
 * @returns {Promise<Object|null>} The added note document data if successful, otherwise null.
 */
export async function pushNote(data) {
	return await pushDocument("notes", data);
}

/**
 * Updates an existing note document.
 * @param {string} docId - The ID of the note document to update.
 * @param {Object} data - The new data for the note.
 * @returns {Promise<Object|null>} The updated note document data if successful, otherwise null.
 */
export async function updateNote(docId, data) {
	return await updateDocument("notes", docId, data);
}

/**
 * Deletes a note document.
 * @param {string} docId - The ID of the note document to delete.
 * @returns {Promise<Object|null>} The deleted note document data if successful, otherwise null.
 */
export async function deleteNote(docId) {
	return await deleteDocument("notes", docId);
}
/* Notes functions [END] */

/* KiloWatt functions [START] */

/**
 * Get last kilowatt document.
 * @returns {Promise<Object|null>} The last kilowatt document data if successful, otherwise null.
 */
export async function getLastKW() {
	const kwCollection = collection(firestore, "kilowatt");
	const q = query(kwCollection, orderBy("timestamp", "desc"), limit(1));
	const snapshot = await getDocs(q);
	if (!snapshot.empty) {
		const doc = snapshot.docs[0];
		return { id: doc.id, ...doc.data() };
	}
	return null;
}

/**
 * Adds a new kilowatt document.
 * @param {Object} data - The data for the kilowatt document.
 * @returns {Promise<Object|null>} The added kilowatt document data if successful, otherwise null.
 */
export async function pushKW(data) {
	return await pushDocument("kilowatt", data);
}

/**
 * Updates an existing kilowatt document.
 * @param {string} docId - The ID of the kilowatt document to update.
 * @param {Object} data - The new data for the kilowatt document.
 * @returns {Promise<Object|null>} The updated kilowatt document data if successful, otherwise null.
 */
export async function updateKW(docId, data) {
	return await updateDocument("kilowatt", docId, data);
}

/**
 * Deletes a kilowatt document.
 * @param {string} docId - The ID of the kilowatt document to delete.
 * @returns {Promise<Object|null>} The deleted kilowatt document data if successful, otherwise null.
 */
export async function deleteKW(docId) {
	return await deleteDocument("kilowatt", docId);
}
/* KiloWatt functions [END] */
